import { removeTrapFocus } from '@theme/focus';

/**
 * Fandom mobile menu panel: <fd-menu-drawer>, rendered by snippets/fd-menu-drawer.liquid as the content of Horizon's
 * <header-drawer><details class="menu-drawer-container" scroll-lock>.
 *
 * Opening and closing stay with assets/header-drawer.js: it toggles the `menu-open` class on the <details>, closes on
 * Escape (keyup inside <header-drawer>) and on the backdrop / close button (`on:click="header-drawer/close"`); the
 * `scroll-lock` attribute makes assets/dialog.js lock the page scroll. This element only follows the `menu-open` class:
 * - Focus: header-drawer.js traps focus on the whole <details> (the menu icon behind the panel included, hidden
 *   controls counted). While the panel is open this element replaces that trap with one limited to the visible
 *   controls of the panel, moves focus to the close button, and gives focus back to the menu icon on close.
 * - Escape pressed while focus is outside <header-drawer> (for example after a tap on a non-focusable area).
 * - The search field closes the panel and opens the existing search modal (#search-modal).
 * - Accordions: one open at a time (`[data-fd-acc]` buttons with aria-expanded / aria-controls).
 * - Region screen ([data-fd-screen], snippets/fd-region-panel.liquid mode 'screen'): the pinned row opens it over the
 *   panel (the rest of the panel becomes inert), its back button and Escape return to the menu, its close button
 *   closes everything.
 * - Wishlist: assets/fd-wishlist.js fires `fd-wishlist:open` before opening ReStock's list; the panel closes first.
 * - Theme editor: selecting a block printed in the panel (a quick tile) opens the panel at phone width; deselecting
 *   it closes the panel unless another block of the panel is selected right after (moving from tile to tile).
 */

const FOCUSABLE =
  'summary, a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
// Hooks inside snippets/fd-region-panel.liquid (mode 'screen').
const REGION_BACK = '[data-fd-region-back]';
const REGION_CLOSE = '[data-fd-region-close]';

class FdMenuDrawer extends HTMLElement {
  /** @type {HTMLDetailsElement | null} */
  #details = null;
  /** @type {(HTMLElement & { close?: () => void }) | null} */
  #drawer = null;
  /** @type {HTMLElement | null} */
  #summary = null;
  /** @type {MutationObserver | null} */
  #observer = null;
  #isOpen = false;
  #restoreFocus = true;
  #trapTimer = 0;
  #deselectTimer = 0;
  /** An Escape keydown already handled here (focus was outside the drawer): its keyup must not close the panel. */
  #escapeHandled = false;

  connectedCallback() {
    this.#details = this.closest('details');
    this.#drawer = this.closest('header-drawer');
    if (!this.#details || !this.#drawer) return;

    this.#summary = this.#details.querySelector(':scope > summary');
    this.#observer = new MutationObserver(this.#sync);
    this.#observer.observe(this.#details, { attributes: true, attributeFilter: ['class'] });
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keyup', this.#onKeyup);
    document.addEventListener('shopify:block:select', this.#onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.#onBlockDeselect);
    document.addEventListener('fd-wishlist:open', this.#onWishlistOpen);
    this.#sync();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keyup', this.#onKeyup);
    document.removeEventListener('shopify:block:select', this.#onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.#onBlockDeselect);
    document.removeEventListener('fd-wishlist:open', this.#onWishlistOpen);
    window.clearTimeout(this.#trapTimer);
    window.clearTimeout(this.#deselectTimer);
    this.#release();
  }

  #sync = () => {
    const open = !!this.#details?.classList.contains('menu-open');
    if (open === this.#isOpen) return;
    this.#isOpen = open;
    if (open) this.#onOpen();
    else this.#onClose();
  };

  #onOpen() {
    this.#restoreFocus = true;
    window.clearTimeout(this.#trapTimer);
    // header-drawer.js adds `menu-open` in a requestAnimationFrame callback and, in the same callback, queues
    // trapFocus(<details>) with setTimeout(0). This observer runs right after that callback, so this timeout is queued
    // after Horizon's: the panel trap replaces Horizon's trap.
    this.#trapTimer = window.setTimeout(() => {
      if (!this.#isOpen) return;
      removeTrapFocus();
      document.addEventListener('keydown', this.#onKeydown, true);
      document.addEventListener('focusin', this.#onFocusin, true);
      const close = this.querySelector('.fd-hd-drawer__close');
      const target = close instanceof HTMLElement ? close : this.#focusables()[0];
      this.#focusWhenShown(target, 10);
    }, 0);
  }

  /**
   * Focus `target`; if the panel is not rendered yet (focus is refused while it is hidden), try again on the next
   * frames.
   * @param {HTMLElement | undefined} target
   * @param {number} tries
   */
  #focusWhenShown(target, tries) {
    if (!target || !this.#isOpen) return;
    target.focus({ preventScroll: true });
    if (document.activeElement !== target && tries > 0) {
      requestAnimationFrame(() => this.#focusWhenShown(target, tries - 1));
    }
  }

  #onClose() {
    window.clearTimeout(this.#trapTimer);
    this.#release();
    // The next opening starts on the menu, not on the region screen.
    this.#closeScreen(false);
    if (this.#restoreFocus) this.#summary?.focus({ preventScroll: true });
    this.#restoreFocus = true;
  }

  #release() {
    document.removeEventListener('keydown', this.#onKeydown, true);
    document.removeEventListener('focusin', this.#onFocusin, true);
  }

  /**
   * Controls a keyboard user can reach inside `root`: rendered and visible, not in a [hidden] or [inert] part, not
   * inside a closed <details> (except that <details>' own summary), tabindex not negative.
   * @param {Element} [root]
   * @returns {HTMLElement[]}
   */
  #focusables(root = this) {
    return /** @type {HTMLElement[]} */ ([...root.querySelectorAll(FOCUSABLE)].filter((el) => this.#reachable(el)));
  }

  /** @param {Element} el */
  #reachable(el) {
    if (!(el instanceof HTMLElement) || el.tabIndex < 0) return false;
    if (el.closest('[hidden], [inert]') || el.getClientRects().length === 0) return false;
    if (getComputedStyle(el).visibility === 'hidden') return false;
    for (let details = el.closest('details'); details && this.contains(details); ) {
      if (!details.open && !details.querySelector(':scope > summary')?.contains(el)) return false;
      details = details.parentElement?.closest('details') ?? null;
    }
    return true;
  }

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key === 'Escape') {
      this.#escapeHandled = false;
      // Inside <header-drawer>, header-drawer.js closes on keyup (#onKeyup turns that into "back" while the region
      // screen is open); only handle focus that is outside it.
      const inside = event.target instanceof Node && !!this.#drawer?.contains(event.target);
      if (inside) return;
      if (this.#screenOpen()) {
        // Focus lands on the region row now, so the matching keyup fires inside <header-drawer>: swallow it.
        this.#escapeHandled = true;
        this.#closeScreen(true);
      } else {
        this.#drawer?.close?.();
      }
      return;
    }
    if (event.key !== 'Tab') return;

    const items = this.#focusables();
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;

    if (!(active instanceof Node) || !this.contains(active)) {
      event.preventDefault();
      first.focus();
    } else if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  /** @param {FocusEvent} event */
  #onFocusin = (event) => {
    if (event.target instanceof Node && this.contains(event.target)) return;
    event.stopPropagation();
    this.#focusables()[0]?.focus({ preventScroll: true });
  };

  /**
   * Escape keyup inside the panel, before it reaches header-drawer.js (which listens for keyup on <header-drawer>):
   * - in the region screen it goes back to the menu instead of closing the panel;
   * - in a <details> printed inside the panel (none today) it closes only that <details>: header-drawer.js would
   *   close it too, but then re-trap focus on its whole drawer (the menu icon behind the panel included).
   * @param {KeyboardEvent} event
   */
  #onKeyup = (event) => {
    if (event.key !== 'Escape') return;
    if (this.#escapeHandled) {
      this.#escapeHandled = false;
      event.stopPropagation();
      return;
    }
    if (this.#screenOpen()) {
      event.stopPropagation();
      this.#closeScreen(true);
      return;
    }
    const details = event.target instanceof Element ? event.target.closest('details') : null;
    if (details && details.open && this.contains(details)) {
      event.stopPropagation();
      details.open = false;
      details.querySelector(':scope > summary')?.focus({ preventScroll: true });
    }
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const search = target.closest('[data-fd-drawer-search]');
    if (search instanceof HTMLElement) {
      event.preventDefault();
      this.#openSearch(search);
      return;
    }

    const accordion = target.closest('[data-fd-acc]');
    if (accordion instanceof HTMLElement) {
      this.#toggleAccordion(accordion);
      return;
    }

    const regionOpen = target.closest('[data-fd-region-open]');
    if (regionOpen instanceof HTMLElement) {
      this.#openScreen(regionOpen);
      return;
    }

    if (target.closest(`[data-fd-screen] ${REGION_BACK}`)) {
      event.preventDefault();
      this.#closeScreen(true);
      return;
    }

    if (target.closest(`[data-fd-screen] ${REGION_CLOSE}`)) {
      event.preventDefault();
      this.#drawer?.close?.();
    }
  };

  /**
   * One accordion open at a time; tapping the open one closes it.
   * @param {HTMLElement} button
   */
  #toggleAccordion(button) {
    const willOpen = button.getAttribute('aria-expanded') !== 'true';
    for (const item of this.querySelectorAll('[data-fd-acc]')) {
      const expanded = item === button ? willOpen : false;
      item.setAttribute('aria-expanded', String(expanded));
      const id = item.getAttribute('aria-controls');
      const body = id ? this.querySelector(`#${CSS.escape(id)}`) : null;
      if (body instanceof HTMLElement) body.hidden = !expanded;
    }
  }

  #screen() {
    const screen = this.querySelector('[data-fd-screen]');
    return screen instanceof HTMLElement ? screen : null;
  }

  #screenOpen() {
    const screen = this.#screen();
    return !!screen && !screen.hidden;
  }

  /** @param {HTMLElement} opener */
  #openScreen(opener) {
    const screen = this.#screen();
    if (!screen) return;
    screen.hidden = false;
    for (const part of this.querySelectorAll('[data-fd-drawer-part]')) part.setAttribute('inert', '');
    opener.setAttribute('aria-expanded', 'true');
    const back = screen.querySelector(REGION_BACK);
    const target = back instanceof HTMLElement ? back : this.#focusables(screen)[0];
    target?.focus({ preventScroll: true });
  }

  /** @param {boolean} focusOpener */
  #closeScreen(focusOpener) {
    const screen = this.#screen();
    if (!screen || screen.hidden) return;
    screen.hidden = true;
    for (const part of this.querySelectorAll('[data-fd-drawer-part]')) part.removeAttribute('inert');
    const opener = this.querySelector('[data-fd-region-open]');
    opener?.setAttribute('aria-expanded', 'false');
    if (focusOpener && opener instanceof HTMLElement) opener.focus({ preventScroll: true });
  }

  #onWishlistOpen = () => {
    if (!this.#isOpen) return;
    // ReStock's list takes focus: release the trap first and do not move focus back to the menu icon.
    this.#restoreFocus = false;
    this.#release();
    this.#drawer?.close?.();
  };

  /**
   * @param {Event} event
   * @returns {boolean} Whether the theme-editor event is about a block printed in this panel.
   */
  #ownsBlock(event) {
    return event.target instanceof Node && this.contains(event.target);
  }

  /**
   * Whether the drawer is open or opening. header-drawer.js sets the summary's aria-expanded synchronously in open()
   * and close(), while `menu-open` is added one frame later and this element's #isOpen follows it later still.
   */
  #expanded() {
    return this.#summary?.getAttribute('aria-expanded') === 'true';
  }

  /**
   * Theme editor: open the panel when one of its blocks is selected (a pending close from the previous block's
   * deselect is cancelled).
   * @param {Event} event
   */
  #onBlockSelect = (event) => {
    if (!this.#ownsBlock(event)) return;
    window.clearTimeout(this.#deselectTimer);
    this.#openForEditor();
  };

  /**
   * Theme editor: close the panel when its block is deselected, unless a block of the panel is selected right after
   * (the editor fires deselect for the old block, then select for the new one).
   * @param {Event} event
   */
  #onBlockDeselect = (event) => {
    if (!this.#ownsBlock(event)) return;
    window.clearTimeout(this.#deselectTimer);
    this.#deselectTimer = window.setTimeout(() => {
      if (this.#expanded()) this.#drawer?.close?.();
    }, 250);
  };

  #openForEditor() {
    const details = this.#details;
    const drawer = /** @type {(HTMLElement & { open?: () => void }) | null} */ (this.#drawer);
    if (!details || !drawer || this.#expanded()) return;
    // Desktop preview: the menu icon and the panel are not rendered. Opening would only lock the page scroll
    // (the <details> has scroll-lock) behind a panel nobody sees.
    if (!this.#summary || this.#summary.getClientRects().length === 0) return;
    if (details.open) {
      // A close is still running: header-drawer.js removes [open] when its transition ends. Re-adding `menu-open`
      // now would cancel that transition and its pending reset would close the panel again, so open after it.
      details.addEventListener(
        'toggle',
        () => {
          if (!details.open) this.#openForEditor();
        },
        { once: true }
      );
      return;
    }
    // Like assets/theme-editor.js: set the <details> open first (header-drawer.js only adds the `menu-open` class).
    details.open = true;
    drawer.open?.();
  }

  /** @param {HTMLElement} button */
  #openSearch(button) {
    const modal = /** @type {(HTMLElement & { showDialog?: () => void }) | null} */ (
      document.getElementById('search-modal')
    );
    // The search modal takes focus: release the trap first and do not move focus back to the menu icon.
    this.#restoreFocus = false;
    this.#release();
    this.#drawer?.close?.();
    if (modal && typeof modal.showDialog === 'function') {
      modal.showDialog();
    } else if (button.dataset.searchUrl) {
      window.location.href = button.dataset.searchUrl;
    }
  }
}

if (!customElements.get('fd-menu-drawer')) customElements.define('fd-menu-drawer', FdMenuDrawer);
